import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Loader2, CheckCircle } from 'lucide-react';
import axios from 'axios';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { contactInfo } from '../data/mock';
import { toast } from 'sonner';
import { submitToWeb3Forms } from '../lib/forms';
import { getOptimizedImageUrl } from '../lib/utils';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    school: '',
    studentAge: '',
    parentName: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        school: formData.school,
        age: formData.studentAge,
        parent_name: formData.parentName,
        message: formData.message,
        submission_type: 'application'
      };

      await submitToWeb3Forms(payload, {
        formName: 'FIRST Global Application Form',
        subject: `New Application: ${formData.name} (${formData.school})`
      });

      toast.success('Application submitted successfully!');
      setIsSubmitted(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="contact-page">
      <Header />

      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-20 md:py-28 overflow-hidden bg-[#FCEEE3]">
          {/* Background Image with Overlay */}
          <div className="absolute inset-0">
            <img
              src={getOptimizedImageUrl('/assets/images/contact_page_india.webp', 1920)}
              srcSet={`
                ${getOptimizedImageUrl('/assets/images/contact_page_india.webp', 800)} 800w,
                ${getOptimizedImageUrl('/assets/images/contact_page_india.webp', 1200)} 1200w,
                ${getOptimizedImageUrl('/assets/images/contact_page_india.webp', 1920)} 1920w
              `}
              sizes="100vw"
              alt="Apply for FIRST Global 2027"
              className="w-full h-full object-cover object-bottom opacity-85"
              loading="eager"
            />
            {/* Soft gradient overlay to blend top and bottom */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#FCEEE3]/40 via-transparent to-cream-dark" />
            {/* Fade out the "WELCOME TO INDIA" text and lines at the very bottom */}
            <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-cream-dark via-cream-dark/80 to-transparent" />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto">
              <div className="bg-cream/75 border border-pragmatic/30 rounded-3xl p-8 md:p-10 shadow-xl shadow-dwarfpony/5 hover:shadow-2xl transition-all duration-300">
                <span className="inline-block text-dwarfpony text-xs font-bold tracking-widest uppercase mb-4 px-4 py-1.5 bg-dwarfpony/10 rounded-full border border-dwarfpony/20">
                  Get in Touch
                </span>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-midnight mb-6 leading-tight tracking-tight">
                  Apply for <span className="block sm:inline text-transparent bg-clip-text bg-gradient-to-r from-dwarfpony to-dwarfpony-dark">FIRST Global 2027</span>
                </h1>
                <p className="text-lg md:text-xl text-deepwalnut-dark leading-relaxed font-semibold max-w-2xl mx-auto">
                  Take the first step towards representing India on the world stage
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 bg-cream-dark">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-3 gap-12">
              {/* Contact Info */}
              <div className="lg:col-span-1 space-y-8">
                <div>
                  <h2 className="text-2xl font-bold text-deepwalnut mb-6">Contact Information</h2>
                  <p className="text-heavyrain">
                    Have questions? Reach out to us and our team will get back to you within 24 hours.
                  </p>
                </div>

                <div className="space-y-6">
                  <div className="flex items-start gap-4 p-4 bg-cream rounded-xl border border-pragmatic/30 shadow-sm">
                    <div className="w-12 h-12 rounded-xl bg-pragmatic/30 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-dwarfpony" />
                    </div>
                    <div>
                      <p className="text-heavyrain text-sm">Email</p>
                      <a href={`mailto:${contactInfo.email}`} className="text-deepwalnut hover:text-dwarfpony transition-colors">
                        {contactInfo.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-cream rounded-xl border border-pragmatic/30 shadow-sm">
                    <div className="w-12 h-12 rounded-xl bg-richolive/20 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-richolive" />
                    </div>
                    <div>
                      <p className="text-heavyrain text-sm">Phone</p>
                      <a href={`tel:${contactInfo.phone}`} className="text-deepwalnut hover:text-richolive transition-colors">
                        {contactInfo.phone}
                      </a>
                    </div>
                  </div>

                  {contactInfo.address && (
                    <div className="flex items-start gap-4 p-4 bg-cream rounded-xl border border-pragmatic/30 shadow-sm">
                      <div className="w-12 h-12 rounded-xl bg-pragmatic/30 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-6 h-6 text-dwarfpony" />
                      </div>
                      <div>
                        <p className="text-heavyrain text-sm">Address</p>
                        <p className="text-deepwalnut">{contactInfo.address}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Eligibility */}
                <div className="bg-gradient-to-br from-pragmatic/20 to-richolive/10 border border-dwarfpony/30 rounded-xl p-6">
                  <h3 className="text-deepwalnut font-bold mb-4">Eligibility Criteria</h3>
                  <ul className="space-y-3 text-sm">
                    <li className="flex items-start gap-2 text-deepwalnut">
                      <CheckCircle size={16} className="text-richolive mt-0.5 flex-shrink-0" />
                      Age between 14-18 years
                    </li>
                    <li className="flex items-start gap-2 text-deepwalnut">
                      <CheckCircle size={16} className="text-richolive mt-0.5 flex-shrink-0" />
                      Currently enrolled in school
                    </li>
                    <li className="flex items-start gap-2 text-deepwalnut">
                      <CheckCircle size={16} className="text-richolive mt-0.5 flex-shrink-0" />
                      Interest in STEM and robotics
                    </li>
                    <li className="flex items-start gap-2 text-deepwalnut">
                      <CheckCircle size={16} className="text-richolive mt-0.5 flex-shrink-0" />
                      Willingness to commit 6+ months
                    </li>
                  </ul>
                </div>
              </div>

              {/* Application Form */}
              <div className="lg:col-span-2">
                <div className="bg-cream border border-pragmatic/30 rounded-2xl p-8 shadow-lg">
                  {isSubmitted ? (
                    <div className="text-center py-12">
                      <div className="w-20 h-20 rounded-full bg-richolive/20 flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="w-10 h-10 text-richolive" />
                      </div>
                      <h3 className="text-2xl font-bold text-deepwalnut mb-4">Message Submitted!</h3>
                      <p className="text-heavyrain max-w-md mx-auto">
                        Thank you for your interest in FIRST Global. Our team will review your message and contact you soon.
                      </p>
                      <Button
                        onClick={() => {
                          setIsSubmitted(false);
                          setFormData({ name: '', email: '', phone: '', school: '', studentAge: '', parentName: '', message: '' });
                        }}
                        className="mt-6 bg-gradient-to-r from-dwarfpony to-dwarfpony-dark hover:from-dwarfpony-dark hover:to-dwarfpony text-white"
                      >
                        Submit Another Message
                      </Button>
                    </div>
                  ) : (
                    <>
                      <h2 className="text-2xl font-bold text-deepwalnut mb-2">Send us a message</h2>
                      <p className="text-heavyrain mb-8">Fill out the form below and we'll get back to you within 24 hours.</p>

                      <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-deepwalnut mb-2">Full Name *</label>
                            <Input
                              type="text"
                              name="name"
                              placeholder="Enter your full name"
                              value={formData.name}
                              onChange={handleChange}
                              required
                              data-testid="contact-name-input"
                              className="bg-cream-dark border-pragmatic/30 text-deepwalnut placeholder:text-heavyrain focus:border-dwarfpony"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-deepwalnut mb-2">Email Address *</label>
                            <Input
                              type="email"
                              name="email"
                              placeholder="you@email.com"
                              value={formData.email}
                              onChange={handleChange}
                              required
                              data-testid="contact-email-input"
                              className="bg-cream-dark border-pragmatic/30 text-deepwalnut placeholder:text-heavyrain focus:border-dwarfpony"
                            />
                          </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-deepwalnut mb-2">Phone Number *</label>
                            <Input
                              type="tel"
                              name="phone"
                              placeholder="Enter phone number"
                              value={formData.phone}
                              onChange={handleChange}
                              required
                              data-testid="contact-phone-input"
                              className="bg-cream-dark border-pragmatic/30 text-deepwalnut placeholder:text-heavyrain focus:border-dwarfpony"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-deepwalnut mb-2">Organisation/School</label>
                            <Input
                              type="text"
                              name="school"
                              placeholder="School or organisation name"
                              value={formData.school}
                              onChange={handleChange}
                              data-testid="contact-school-input"
                              className="bg-cream-dark border-pragmatic/30 text-deepwalnut placeholder:text-heavyrain focus:border-dwarfpony"
                            />
                          </div>
                        </div>

                        {/* <div className="grid md:grid-cols-2 gap-6">
                          
                          <div>
                            <label className="block text-sm font-medium text-deepwalnut mb-2">Student Age *</label>
                            <Input
                              type="number"
                              name="studentAge"
                              placeholder="Age (14-18)"
                              min="14"
                              max="18"
                              value={formData.studentAge}
                              onChange={handleChange}
                              required
                              data-testid="contact-age-input"
                              className="bg-cream-dark border-pragmatic/30 text-deepwalnut placeholder:text-heavyrain focus:border-dwarfpony"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-deepwalnut mb-2">Parent/Guardian Name</label>
                            <Input
                              type="text"
                              name="parentName"
                              placeholder="Enter parent's name"
                              value={formData.parentName}
                              onChange={handleChange}
                              data-testid="contact-parent-input"
                              className="bg-cream-dark border-pragmatic/30 text-deepwalnut placeholder:text-heavyrain focus:border-dwarfpony"
                            />
                          </div>
                        </div> */}

                        <div>
                          <label className="block text-sm font-medium text-deepwalnut mb-2">Why do you want to participate? *</label>
                          <Textarea
                            name="message"
                            placeholder="What would you like to know? We're happy to help"
                            value={formData.message}
                            onChange={handleChange}
                            required
                            rows={5}
                            data-testid="contact-message-input"
                            className="bg-cream-dark border-pragmatic/30 text-deepwalnut placeholder:text-heavyrain focus:border-dwarfpony resize-none"
                          />
                        </div>

                        <Button
                          type="submit"
                          disabled={isSubmitting}
                          data-testid="contact-submit-btn"
                          className="w-full bg-gradient-to-r from-dwarfpony to-dwarfpony-dark hover:from-dwarfpony-dark hover:to-dwarfpony text-white font-semibold py-6 text-lg shadow-lg shadow-dwarfpony/25 transition-all"
                        >
                          {isSubmitting ? (
                            <>
                              <Loader2 size={20} className="mr-2 animate-spin" />
                              Sending Message...
                            </>
                          ) : (
                            <>
                              <Send size={20} className="mr-2" />
                              Send Message
                            </>
                          )}
                        </Button>
                      </form>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
